/* eslint-disable max-lines */
import * as React from "react";
import { List, Segment, Header } from "semantic-ui-react";

import { AccordionQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireContainer";
import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { AnsweredQuestionWithId, QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { GENDER_OPTIONS } from "@teamprevent/common/utils";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { YesNoNaDropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/YesNoNaDropdownQuestion";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";

import { BbvSourcePatientRiskAssessmentSection } from "./bbvSourcePatientRiskAssessmentSection";
import { BbvPepRiskAssessmentSection } from "./bbvPepRiskAssessmentSection";

export const QUESTIONNAIRE_KEY = "bbvConsultation";
const hepBStatusQuestionConfigId: string = "HepBStatus";
const hepBStatusNeverVaccinated: string = "Never Vaccinated";

interface BbvReportQuestionnaireProps {
    readonly: boolean;
    employeeName: string;
    extraSections?: { title: string; id: string; content: React.ReactNode }[];
    questions: AnsweredQuestionWithId[];
    startAssessment?: () => void;
    loadQuestionnaire?: () => void;
    submitQuestionnaire?: (questions: AnsweredQuestionWithId[]) => Promise<boolean>;
    task?: TaskModel;
    removeAppointment?: (ohCaseId: string, taskId: string) => Promise<void>;
}

export class BbvConsultationQuestionnaire extends React.Component<BbvReportQuestionnaireProps> {
    public render() {
        return (
            <AccordionQuestionnaireContainer
                loadQuestionnaire={this.props.loadQuestionnaire ?? ((): void => {})}
                submitQuestionnaire={this.props.submitQuestionnaire ?? (() => Promise.resolve(true))}
                readonly={this.props.readonly}
                questionnaireKey={QUESTIONNAIRE_KEY}
                questionnaireType={QuestionnaireTypes.ClinicalNotes}
                task={this.props.task}
                removeAppointment={this.props.removeAppointment}
            >
                <AccordionQuestionnaireSection
                    title="Personal Details"
                    id="PersonalDetails"
                >
                    <p>For confidentiality reasons before we proceed I need to just ask you a few security questions?</p>
                    <CheckboxQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "ConfirmName", questionText: `Name confirmed? (${this.props.employeeName})` })}
                        value="Confirmed"
                    />
                    {(this.getAnswerToQuestionWithId("Gender")) && <DropdownQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "Gender", questionText: "Gender" })}
                        options={GENDER_OPTIONS}
                    />}
                    <TextQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "ContactNumber", questionText: "Contact number" })}
                    />
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    title="Line Manager"
                    id="LineManager"
                >
                    <TextQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "LineManagerName", questionText: "Line manager's full name" })}
                        mandatory
                    />
                    <TextQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "LineManagerContactNumber", questionText: "Line manager's contact number" })}
                        mandatory
                    />
                    <MultiButtonQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "LineManagerInformed", questionText: "Has the line manager been informed by employee?" })}
                    />
                    <MultiButtonQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "IncidentFormCompleted", questionText: "Has an accident/incident form been completed?" })}
                    />
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    title="Incident"
                    id="Incident"
                >
                    <OldDateQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "DateOfIncident", questionText: "Date of Incident" })}
                        mandatory
                    />
                    <TextQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "TimeOfIncident", questionText: "Time of Incident (00:00)" })}
                        mandatory
                    />
                    <TextQuestion
                        readonly={this.props.readonly}
                        questionConfig={({
                            id: "LocationOfIncident",
                            questionText: "Where was the individual when the incident occurred? e.g Name of Hospital"
                        })}
                        mandatory
                    />
                    <TextQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "WardDepartment", questionText: "Ward/department" })}
                        mandatory
                    />
                    <TextQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "DescriptionOfIncident", questionText: "Brief Description of Incident" })}
                        multiline
                        mandatory
                    />
                    <MultiButtonQuestion
                        readonly={this.props.readonly}
                        questionConfig={({
                            id: "ReportedOutOfHours",
                            // eslint-disable-next-line max-len
                            questionText: "Was the Injury reported out of hours and the individual obtained treatment or assessment from A&E or another source before speaking to Optima Health?"
                        })}
                    />
                    {this.getAnswerToQuestionWithId("ReportedOutOfHours") === MultiButtonQuestion.YesOption &&
                        <>
                            <MultiButtonQuestion
                                readonly={this.props.readonly}
                                questionConfig={({ id: "AttendAAndE", questionText: "Did the individual attend A&E?" })}
                            />
                            <OldDateQuestion
                                readonly={this.props.readonly}
                                questionConfig={({ id: "DateAttendedOtherAssessment", questionText: "Date attended" })}
                            />
                            <TextQuestion
                                readonly={this.props.readonly}
                                questionConfig={({ id: "WhichHospitalClinicAttended", questionText: "Which hospital/clinic was attended?" })}
                            />
                        </>}

                    <TextQuestion
                        readonly={this.props.readonly}
                        questionConfig={({
                            id: "ActionsAlreadyTaken",
                            questionText: "What actions have already been taken (e.g. serum save, baseline bloods for PEP)?"
                        })}
                        mandatory
                        multiline
                    />
                    <DropdownQuestion
                        mandatory
                        questionConfig={({ id: "ClassificationOfInjuryExpose", questionText: "Classification of Injury and Exposure" })}
                        readonly={this.props.readonly}
                        upward={false}
                        options={[
                            { value: "Low Risk Bodily Fluids", text: "Low Risk Bodily Fluids" },
                            { value: "Low Risk Injury", text: "Low Risk Injury" },
                            { value: "Mucotaneous Exposure", text: "Mucotaneous Exposure" },
                            { value: "Percutaneous Injury", text: "Percutaneous Injury" }
                        ]}
                        /* eslint-disable max-len */
                        popupContent={<List bulleted>
                            <List.Item><strong>Low Risk Bodily Fluids</strong> - these fluids are not considered a risk UNLESS they are visibly blood stained: urine, vomit saliva, faeces, nasal secretions, sputum, sweat, tears</List.Item>
                            <List.Item><strong>Low risk injury</strong> - Splash on intact skin (there is no known risk of BBV transmission from exposures to intact skin)</List.Item>
                            <List.Item><strong>Mucotaneous Exposure</strong> - is an exposure where there is direct contact of blood/body fluid with eyes, nose & mouth or on broken skin e.g. uncovered cuts, abrasions or eczema not covered with waterproof dressing)</List.Item>
                            <List.Item><strong>Percutaneous Injury (including bites)</strong> - is an exposure incident in which penetration of skin occurs by a needle or other sharp object which may have been in contact with blood, tissue, or other body fluid before the exposure.</List.Item>
                        </List> /* eslint-enable max-len */
                        }
                    />
                    <DropdownQuestion
                        mandatory
                        questionConfig={({ id: "TypeOfInjury", questionText: "Type of Injury" })}
                        readonly={this.props.readonly}
                        upward={false}
                        options={[
                            { value: "Exposure with no or minimal risk", text: "Exposure with no or minimal risk" },
                            { value: "High risk exposures", text: "High risk exposures" },
                            { value: "Low risk exposure", text: "Low risk exposure" }
                        ]}
                        popupContent={<>
                            <strong>Exposure with no or minimal risk:</strong>
                            <List bulleted>
                                <List.Item>Skin not breached</List.Item>
                                <List.Item>Contact of body fluid with intact skin</List.Item>
                                <List.Item>Contact with saliva (non-dental), urine, vomit, or faeces that is not visibly blood stained</List.Item>
                                <List.Item>Needle not used on a patient before injury</List.Item>
                            </List>
                            <strong>High Risk Exposures:</strong>
                            <List bulleted>
                                <List.Item>Deep percutaneous injury</List.Item>
                                <List.Item>Freshly used sharps</List.Item>
                                <List.Item>Visible blood on sharps</List.Item>
                                <List.Item>Needle used on source’s blood vessels</List.Item>
                            </List>
                            <strong>Low risk exposures:</strong>
                            <List bulleted>
                                <List.Item>Superficial injury, exposure through broken skin, mucosal exposure</List.Item>
                                <List.Item>Old discarded sharps</List.Item>
                                <List.Item>No visible blood on sharps</List.Item>
                                <List.Item>Needle not used on blood vessels - e.g. suturing, subcutaneous injection needles</List.Item>
                            </List>
                        </>
                        }
                    />
                    <DropdownQuestion
                        questionConfig={({ id: "TypeOfBodilyFluid", questionText: "Type of bodily fluid" })}
                        readonly={this.props.readonly}
                        options={[
                            { value: "Amniotic Tissues or Fluid", text: "Amniotic Tissues or Fluid" },
                            { value: "Blood", text: "Blood" },
                            { value: "Cerebrospinal", text: "Cerebrospinal" },
                            { value: "Faeces", text: "Faeces" },
                            { value: "Nasal Secretions", text: "Nasal Secretions" },
                            { value: "No Contaminant", text: "No Contaminant" },
                            { value: "Pericardial", text: "Pericardial" },
                            { value: "Peritoneal", text: "Peritoneal" },
                            { value: "Pleural", text: "Pleural" },
                            { value: "Saliva", text: "Saliva" },
                            { value: "Sputum", text: "Sputum" },
                            { value: "Sweat", text: "Sweat" },
                            { value: "Synovial", text: "Synovial" },
                            { value: "Tears", text: "Tears" },
                            { value: "Urine", text: "Urine" },
                            { value: "Vaginal Secretions", text: "Vaginal Secretions" },
                            { value: "Vomit", text: "Vomit" }
                        ]}
                    />
                    {
                        (() => {
                            if (this.getAnswerToQuestionWithId("TypeOfBodilyFluid") === "Blood") {
                                return ((this.getAnswerToQuestionWithId("FreshOrDriedBlood"))
                                && <DropdownQuestion
                                    questionConfig={({ id: "FreshOrDriedBlood", questionText: "Was the contaminate fresh or dried blood?" })}
                                    readonly={this.props.readonly}
                                    options={[
                                        { value: "Fresh Blood", text: "Fresh Blood" },
                                        { value: "Dried Blood", text: "Dried Blood" },
                                        { value: "Unknown", text: "Unknown" }
                                    ]}
                                />);
                            } else if (this.getAnswerToQuestionWithId("TypeOfBodilyFluid") !== "No Contaminant") {
                                return (<MultiButtonQuestion
                                    questionConfig={({ id: "BodilyFluidStained", questionText: "Was the bodily fluid blood stained?" })}
                                    readonly={this.props.readonly}
                                />);
                            }
                            return undefined;
                        })()
                    }
                </AccordionQuestionnaireSection>
                {this.getAnswerToQuestionWithId("ClassificationOfInjuryExpose") === "Percutaneous Injury" &&
                    <AccordionQuestionnaireSection
                        title="Percutaneous Injury"
                        id="PercutaneousInjury"
                    >
                        <DropdownQuestion
                            mandatory
                            questionConfig={({ id: "InjurySeverity", questionText: "Severity of Injury" })}
                            readonly={this.props.readonly}
                            options={[
                                { value: "Deep", text: "Deep (Deep penetrating wound with/without bleeding)" },
                                { value: "Moderate", text: "Moderate (Skin Penetrated)" },
                                { value: "Superficial", text: "Superficial (Surface Scratch)" }
                            ]}
                        />
                        {this.getAnswerToQuestionWithId("InjurySeverity") === "Deep" &&
                            <TextQuestion
                                readonly={this.props.readonly}
                                questionConfig={({ id: "DetailsDeepPercutaneousInjury", questionText: "Details of deep percutaneous injury" })}
                            />
                        }
                        <MultiButtonQuestion
                            questionConfig={({ id: "WereGlovesWorn", questionText: "Were gloves worn?" })}
                            readonly={this.props.readonly}
                        />
                        <MultiButtonQuestion
                            readonly={this.props.readonly}
                            questionConfig={({ id: "HollowBoreNeedle", questionText: "Hollow Bore Needle?" })}
                        />
                        {this.getAnswerToQuestionWithId("HollowBoreNeedle") === MultiButtonQuestion.YesOption &&
                            <>
                                <TextQuestion
                                    readonly={this.props.readonly}
                                    questionConfig={({ id: "Gauge", questionText: "Gauge" })}
                                />
                                <TextQuestion
                                    readonly={this.props.readonly}
                                    questionConfig={({ id: "UsedFor", questionText: "Used For" })}
                                />
                            </>
                        }
                        {this.getAnswerToQuestionWithId("HollowBoreNeedle") === MultiButtonQuestion.NoOption &&
                            <>
                                <MultiButtonQuestion
                                    readonly={this.props.readonly}
                                    questionConfig={({ id: "SolidNeedle", questionText: "Solid needle?" })}
                                />
                                {this.getAnswerToQuestionWithId("SolidNeedle") === MultiButtonQuestion.NoOption && <TextQuestion
                                    questionConfig={({ id: "SpecifyInstrument", questionText: "Please specify instrument" })}
                                    readonly={this.props.readonly}
                                />
                                }
                            </>
                        }
                        <MultiButtonQuestion
                            readonly={this.props.readonly}
                            questionConfig={({ id: "HumanBite", questionText: "Human Bite?" })}
                        />
                        <DropdownQuestion
                            questionConfig={({ id: "CleanOrUsed", questionText: "Was it clean or used?" })}
                            readonly={this.props.readonly}
                            options={[
                                { value: "Clean", text: "Clean" },
                                { value: "Used", text: "Used" }
                            ]}
                        />
                        <MultiButtonQuestion
                            readonly={this.props.readonly}
                            questionConfig={({ id: "SafetyDeviceUsed", questionText: "Was a safety device being used?" })}
                        />
                        <TextQuestion
                            mandatory
                            readonly={this.props.readonly}
                            questionConfig={({ id: "SiteOfInjury", questionText: "Site of injury" })}
                        />
                        {(this.getAnswerToQuestionWithId("VolumeOfBlood")) &&
                            this.renderVolumeOfBloodQuestionIfRequired()}
                    </AccordionQuestionnaireSection>}
                {this.getAnswerToQuestionWithId("ClassificationOfInjuryExpose") === "Mucotaneous Exposure" &&
                    <AccordionQuestionnaireSection
                        title="Mucotaneous Exposure"
                        id="MucotaneousExposure"
                    >
                        <DropdownQuestion
                            questionConfig={({ id: "SiteOfInjury", questionText: "Site of Injury" })}
                            readonly={this.props.readonly}
                            options={[
                                { value: "Eyes", text: "Eyes" },
                                { value: "Mouth", text: "Mouth" },
                                { value: "Nose", text: "Nose" },
                                { value: "Slash to Broken Skin", text: "Slash to Broken Skin" }
                            ]}
                        />
                        <MultiButtonQuestion
                            readonly={this.props.readonly}
                            questionConfig={({
                                id: "ProtectionWorn",
                                questionText: "Was any form of protection worn at time of incident e.g gloves, mask, apron etc?"
                            })}
                        />
                        {this.getAnswerToQuestionWithId("ProtectionWorn") === MultiButtonQuestion.YesOption &&
                            <TextQuestion
                                readonly={this.props.readonly}
                                questionConfig={({ id: "ProtectionWornDetails", questionText: "Details" })}
                            />
                        }
                        {(this.getAnswerToQuestionWithId("VolumeOfBlood")) &&
                            this.renderVolumeOfBloodQuestionIfRequired()}
                    </AccordionQuestionnaireSection>}
                <AccordionQuestionnaireSection
                    title="First Aid"
                    id="FirstAid"
                >
                    <MultiButtonQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "FirstAidGiven", questionText: "First Aid Given?" })}
                    />
                    {(this.getAnswerToQuestionWithId("Bled")) && <YesNoNaDropdownQuestion
                        mandatory
                        readonly={this.props.readonly}
                        questionConfig={({ id: "Bled", questionText: "Bled?" })}
                    />}
                    {(this.getAnswerToQuestionWithId("WashedIrrigated")) && <YesNoNaDropdownQuestion
                        mandatory
                        readonly={this.props.readonly}
                        questionConfig={({ id: "WashedIrrigated", questionText: "Washed/Irrigated?" })}
                    />}
                    {(this.getAnswerToQuestionWithId("Covered")) && <YesNoNaDropdownQuestion
                        mandatory
                        readonly={this.props.readonly}
                        questionConfig={({ id: "Covered", questionText: "Covered?" })}
                    />}
                    {(this.getAnswerToQuestionWithId("SerumSaveObtained")) && <MultiButtonQuestion
                        questionConfig={({ id: "SerumSaveObtained", questionText: "Has a serum save been obtained?" })}
                        readonly={this.props.readonly}
                        values={[
                            "Serum save obtained",
                            "Serum save not obtained"
                        ]}
                    />}
                    {this.getAnswerToQuestionWithId("SerumSaveObtained") === "Serum save obtained" &&
                        <OldDateQuestion
                            questionConfig={({ id: "DateSerumSaveObtained", questionText: "Date serum save obtained" })}
                            readonly={this.props.readonly}
                        />
                    }
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    title="Vaccination History"
                    id="VaccinationHistory"
                >
                    <DropdownQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: hepBStatusQuestionConfigId, questionText: "Hep B Status" })}
                        options={[
                            { text: "Immune", value: "Immune" },
                            { text: "Never Vaccinated", value: "Never Vaccinated" },
                            { text: "Non-Responder", value: "Non-Responder" },
                            { text: "Part Immunised", value: "Part Immunised" },
                            { text: "Unknown", value: "Unknown" },
                        ]}
                    />
                    {this.props.questions.find(question => question.id === hepBStatusQuestionConfigId)
                        && this.props.questions.find(question => question.id === hepBStatusQuestionConfigId)?.answer !== hepBStatusNeverVaccinated &&
                        <OldDateQuestion
                            readonly={this.props.readonly}
                            questionConfig={({ id: "DateOfLastHepBVaccination", questionText: "Approximate date of last vacciantion" })}
                        />}
                    <DropdownQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "LastAntibodyTitreLevel", questionText: "Last antibody titre level" })}
                        options={[
                            { text: "<10iu/ml", value: "<10iu/ml" },
                            { text: ">100iu/ml", value: ">100iu/ml" },
                            { text: "10-100iu/ml", value: "10-100iu/ml" },
                            { text: "Unknown", value: "Unknown" },
                        ]}
                    />
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    title="Source Details"
                    id="SourceDetails"
                >
                    <MultiButtonQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "SourceKnown", questionText: "Is the Source known?" })}
                    />
                    {this.getAnswerToQuestionWithId("SourceKnown") === MultiButtonQuestion.NoOption &&
                        <MultiButtonQuestion
                            readonly={this.props.readonly}
                            questionConfig={({
                                id: "AnyIndicationOfOrigin",
                                // eslint-disable-next-line max-len
                                questionText: "Is there any indication of the origin of the device or blood fluid? e.g was the device from a unit or ward area with patients known to have Hepatitis B, C or HIV?"
                            })}
                        />}
                    {this.getAnswerToQuestionWithId("SourceKnown") === MultiButtonQuestion.YesOption &&
                        <>
                            <TextQuestion
                                readonly={this.props.readonly}
                                questionConfig={({
                                    id: "PatientName",
                                    questionText: "Patient Name"
                                })}
                            />
                            <OldDateQuestion
                                readonly={this.props.readonly}
                                questionConfig={({
                                    id: "PatientDateOfBirth",
                                    questionText: "Date of Birth"
                                })}
                            />
                            <TextQuestion
                                readonly={this.props.readonly}
                                questionConfig={({
                                    id: "HospitalNumber",
                                    questionText: "Hospital Number"
                                })}
                            />
                            <TextQuestion
                                readonly={this.props.readonly}
                                questionConfig={({
                                    id: "CurrentLocation",
                                    questionText: "Current Location e.g. Hospital, Home etc"
                                })}
                            />
                            <TextQuestion
                                readonly={this.props.readonly}
                                questionConfig={({
                                    id: "ConsultantInChargeOfSourcePatientsCare",
                                    questionText: "Consultant or Senior Nurse in charge of source patient's care if known"
                                })}
                            />
                            <TextQuestion
                                readonly={this.props.readonly}
                                multiline
                                questionConfig={({
                                    id: "SourceKnownFurtherInfo",
                                    questionText: "Any further information"
                                })}
                            />
                        </>}
                </AccordionQuestionnaireSection>
                {this.getAnswerToQuestionWithId("SourceKnown") === MultiButtonQuestion.YesOption &&
                    <BbvSourcePatientRiskAssessmentSection
                        readonly={this.props.readonly}
                        questionnaireKey={QUESTIONNAIRE_KEY}
                        questions={this.props.questions}
                    />
                }
                {(this.getAnswerToQuestionWithId("ClassificationOfInjuryExpose") === "Mucotaneous Exposure" ||
                    this.getAnswerToQuestionWithId("ClassificationOfInjuryExpose") === "Percutaneous Injury") &&
                    <BbvPepRiskAssessmentSection
                        readonly={this.props.readonly}
                        questionnaireKey={QUESTIONNAIRE_KEY}
                        questions={this.props.questions}
                    />
                }
                <AccordionQuestionnaireSection
                    title="Action required"
                    id="ActionRequired"
                >
                    {(this.getAnswerToQuestionWithId("LineManagerContactedArrangeRiskAssessment")) &&
                    <MultiButtonQuestion
                        readonly={this.props.readonly}
                        questionConfig={({
                            id: "LineManagerContactedArrangeRiskAssessment",
                            // eslint-disable-next-line max-len
                            questionText: "Has the employee's line manager been contacted to arrange in-patient risk assessment of source patient and source testing?"
                        })}
                    />}
                    {this.getAnswerToQuestionWithId("LineManagerContactedArrangeRiskAssessment") === MultiButtonQuestion.YesOption &&
                        <TextQuestion
                            readonly={this.props.readonly}
                            questionConfig={({ id: "LineManagerContactedArrangeRiskAssessmentDetails", questionText: "Details" })}
                        />
                    }
                    <MultiButtonQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "ReferredToAE", questionText: "Employee referred to A&E?" })}
                    />
                    {this.getAnswerToQuestionWithId("ReferredToAE") === MultiButtonQuestion.YesOption &&
                        <TextQuestion
                            readonly={this.props.readonly}
                            questionConfig={({ id: "ReferredToAEDetails", questionText: "Details" })}
                        />
                    }
                    {(this.getAnswerToQuestionWithId("ReferredToGumClinic")) &&
                    <MultiButtonQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "ReferredToGumClinic", questionText: "Employee referred to GUM Clinic?" })}
                    />}
                    {this.getAnswerToQuestionWithId("ReferredToGumClinic") === MultiButtonQuestion.YesOption &&
                        <TextQuestion
                            readonly={this.props.readonly}
                            questionConfig={({ id: "ReferredToGumClinicDetails", questionText: "Details" })}
                        />
                    }
                    {(this.getAnswerToQuestionWithId("ReferredToOhClinicForImmediateFaceToFace")) &&
                    <MultiButtonQuestion
                        readonly={this.props.readonly}
                        questionConfig={({
                            id: "ReferredToOhClinicForImmediateFaceToFace",
                            questionText: "Employee referred to OH clinic for immediate face to face follow up?"
                        })}
                    />}
                    {this.getAnswerToQuestionWithId("ReferredToOhClinicForImmediateFaceToFace") === MultiButtonQuestion.YesOption &&
                        <TextQuestion
                            readonly={this.props.readonly}
                            questionConfig={({ id: "ReferredToOhClinicForImmediateFaceToFaceDetails", questionText: "Details" })}
                        />
                    }
                    <MultiButtonQuestion
                        readonly={this.props.readonly}
                        questionConfig={({ id: "FurtherOhActionRequired", questionText: "Is further OH action required?" })}
                    />
                    {this.getAnswerToQuestionWithId("FurtherOhActionRequired") === MultiButtonQuestion.YesOption &&
                        <TextQuestion
                            readonly={this.props.readonly}
                            questionConfig={({ id: "FurtherOhActionRequiredDetails", questionText: "Details" })}
                        />
                    }
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    title="Conclusions"
                    id="Conclusions"
                >
                    <Segment>
                        <Header>Criteria for Insignificant/Significant</Header>
                        <p>Exposures that involve contamination of intact skin are not normally considered significant and require no further action</p>
                        <p>Exposures that are significant include:</p>
                        <List bulleted>
                            <List.Item>Percutaneous Injury</List.Item>
                            <List.Item>Contamination of broken skin (abrasions, cuts, eczema etc)</List.Item>
                            <List.Item>Splashes onto mucous membranes (e.g. eyes or mouth)</List.Item>
                            { /* eslint-disable-next-line max-len */ }
                            <List.Item>A human bite or clenched-fist type injury (sustained when a clenched fist hits a person’s teeth causing small wounds over the metacarpophalangeal joint)</List.Item>
                        </List>
                    </Segment>
                    <DropdownQuestion
                        mandatory
                        readonly={this.props.readonly}
                        questionConfig={({ id: "ExposureInjury", questionText: "Exposure/Injury" })}
                        options={[
                            { text: "Significant", value: "Significant" },
                            { text: "Insignificant", value: "Insignificant" }
                        ]}
                    />
                    <DropdownQuestion
                        mandatory
                        readonly={this.props.readonly}
                        questionConfig={({ id: "HazardSourcePatient", questionText: "Hazard/Source Patient" })}
                        options={[
                            { text: "High", value: "High" },
                            { text: "Low", value: "Low" }
                        ]}
                    />
                    {(this.getAnswerToQuestionWithId("HepatitisBRisk")) && <DropdownQuestion
                        mandatory
                        readonly={this.props.readonly}
                        questionConfig={({ id: "HepatitisBRisk", questionText: "Hepatitis B" })}
                        options={[
                            { text: "High Risk", value: "High Risk" },
                            { text: "Low Risk", value: "Low Risk" }
                        ]}
                    />}
                    {this.getAnswerToQuestionWithId("HepatitisBRisk") === "High Risk" &&
                        <>
                            <Segment>Blood Test for Hepatitis B recommended in 6 weeks if High Risk</Segment>
                            <MultiButtonQuestion
                                questionConfig={({ id: "HbigVaccineCourseRecommended", questionText: "Has HBIG or booster/vaccine course been recommended?" })}
                                readonly={this.props.readonly}
                                values={[
                                    "HBIG or booster/vaccine course been recommended",
                                    "HBIG or booster/vaccine course has not been recommended"
                                ]}
                            />
                        </>}
                    {(this.getAnswerToQuestionWithId("HepatitisCRisk")) && <DropdownQuestion
                        mandatory
                        readonly={this.props.readonly}
                        questionConfig={({ id: "HepatitisCRisk", questionText: "Hepatitis C" })}
                        options={[
                            { text: "High Risk", value: "High Risk" },
                            { text: "Low Risk", value: "Low Risk" }
                        ]}
                    />}
                    {this.getAnswerToQuestionWithId("HepatitisCRisk") === "High Risk" &&
                        <Segment>Blood Test for Hepatitis C recommended in 6 weeks if High Risk</Segment>}
                    {(this.getAnswerToQuestionWithId("HivRisk")) && <DropdownQuestion
                        mandatory
                        readonly={this.props.readonly}
                        questionConfig={({ id: "HivRisk", questionText: "HIV" })}
                        options={[
                            { text: "High Risk", value: "High Risk" },
                            { text: "Low Risk", value: "Low Risk" }
                        ]}
                    />}
                    {this.getAnswerToQuestionWithId("HivRisk") === "High Risk" &&
                        <Segment>PEP and Blood Test for HIV recommended in 12 weeks if High Risk</Segment>}
                    <DropdownQuestion
                        mandatory
                        readonly={this.props.readonly}
                        questionConfig={({ id: "ClientAnxietyRisk", questionText: "Client Anxiety" })}
                        options={[
                            { text: "High", value: "High" },
                            { text: "Medium", value: "Medium" },
                            { text: "Low", value: "Low" }
                        ]}
                    />
                    {(this.getAnswerToQuestionWithId("ClientAnxietyRisk") === "High" || this.getAnswerToQuestionWithId("ClientAnxietyRisk") === "Medium") &&
                        <Segment>Blood Test for Hepatitis B, Hepatitis C and HIV recommended if High or Medium Anxiety</Segment>}
                    <MultiButtonQuestion
                        questionConfig={({ id: "ExposureBloodTestsRecommended", questionText: "Are post exposure blood tests recommended?" })}
                        readonly={this.props.readonly}
                    />
                    <MultiButtonQuestion
                        questionConfig={({
                            id: "AdvisedAboutAvoidance",
                            questionText: "Has the injured person been advised about avoidance of unprotected sex and blood donation?"
                        })}
                        readonly={this.props.readonly}
                    />
                    <TextQuestion
                        mandatory
                        readonly={this.props.readonly}
                        questionConfig={({ id: "AdviceGivenByOHA", questionText: "Advice given by OHA" })}
                        multiline
                    />
                </AccordionQuestionnaireSection>
                {
                    this.props.extraSections && this.props.extraSections.map((s, i) =>
                        (<AccordionQuestionnaireSection key={i} title={s.title} id={s.id} hideNextButton={true} active={true} neverHide={true}>
                            {s.content}
                        </AccordionQuestionnaireSection>))
                }
            </AccordionQuestionnaireContainer>
        );
    }

    private renderVolumeOfBloodQuestionIfRequired() {
        return this.getAnswerToQuestionWithId("TypeOfBodilyFluid") === "Blood" &&
            (
                <DropdownQuestion
                    questionConfig={({ id: "VolumeOfBlood", questionText: "What was the approximate volume of blood" })}
                    readonly={this.props.readonly}
                    options={[
                        { value: "<0.1ml blood", text: "<0.1ml blood" },
                        { value: ">0.1ml blood", text: ">0.1ml blood" }
                    ]}
                />);
    }

    private getAnswerToQuestionWithId(id: string) {
        return this.props.questions.find(question => question.id === id) && this.props.questions.find(question => question.id === id)!.answer;
    }
}
